'use client';

import Cookies from 'js-cookie';
import { useParams } from 'next/navigation';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { ParticipantEnum } from '@/constants/common';
import { useNavigate } from '@/hooks/useNavigate';
import { getParticipantByParam } from '@/utils/helpers/getParticipantByParam';

type ParticipantContextProps = {
  isResumes: boolean;
  isVacancies: boolean;
  participant: ParticipantEnum;
  setParticipant: (participant: ParticipantEnum) => void;
  loading: boolean;
};

type ParticipantProviderProps = {
  cookieKey?: string;
  children: ReactNode;
  initialParticipant: ParticipantEnum;
  cookieDomain?: string;
};

const ParticipantContext = createContext<ParticipantContextProps | undefined>(
  undefined
);

const defaultContext: ParticipantContextProps = {
  isResumes: false,
  isVacancies: true,
  setParticipant: () => {},
  participant: ParticipantEnum.VACANCY,
  loading: false
};

export const useParticipant = () =>
  useContext(ParticipantContext) ?? defaultContext;

const Participant = ({
  cookieKey = 'participant',
  children,
  initialParticipant,
  cookieDomain
}: ParticipantProviderProps) => {
  const params = useParams();
  const { navigate } = useNavigate();

  const [participant, setParticipantState] = useState<ParticipantEnum | null>(
    initialParticipant
  );
  const [loading, setLoading] = useState(true);

  const setParticipant = useCallback(
    (newParticipant: ParticipantEnum, withNavigate?: boolean) => {
      if (withNavigate) navigate({ participant: newParticipant });
      else setParticipantState(newParticipant);
      Cookies.set(cookieKey, newParticipant, {
        path: '/',
        domain: cookieDomain || undefined,
        expires: 30
      });
      document.documentElement.setAttribute('data-participant', newParticipant);
    },
    [cookieKey, params, cookieDomain]
  );

  useEffect(() => {
    const storedParticipant = getParticipantByParam(
      (params.participant as string) ?? Cookies.get(cookieKey)
    );

    if (storedParticipant) {
      setParticipant(storedParticipant);
    } else {
      setParticipant(ParticipantEnum.VACANCY);
    }

    setLoading(false);
  }, [cookieKey, setParticipant, params]);

  const isResumes = participant === ParticipantEnum.RESUME;
  const isVacancies = participant === ParticipantEnum.VACANCY;

  const providerValue = useMemo(() => {
    if (participant === null) return undefined;

    return {
      participant,
      setParticipant: (newParticipant: ParticipantEnum) =>
        setParticipant(newParticipant, true),
      isResumes,
      isVacancies,
      loading
    };
  }, [participant, setParticipant, loading, params]);

  if (!providerValue) return null;

  return (
    <ParticipantContext.Provider value={providerValue}>
      {children}
    </ParticipantContext.Provider>
  );
};

export const ParticipantProvider = (props: ParticipantProviderProps) => {
  const context = useContext(ParticipantContext);

  if (context) return <>{props.children}</>;
  return <Participant {...props} />;
};
